import { type ReactNode } from 'react';
import { SegmentBar } from './primitives/SegmentBar';
import { TableDirection } from './directions/TableDirection';
import { PipelineDirection } from './directions/PipelineDirection';
import { HybridDirection } from './directions/HybridDirection';
import type { LoanCenterDirection } from './use-loan-center-direction';
import { useLoanListLayout } from './loan-list-layout';
import type { ProspectAnalytics, ProspectFilters, ProspectLoan, ProspectSortConfig } from '../types';

type Segment = NonNullable<ProspectFilters['segment']>;

interface LoanCenterRedesignProps {
  direction: LoanCenterDirection | null;
  directionTabsSlot: ReactNode;
  /** Toolbar — ProspectFiltersComponent, rendered by the dashboard. */
  toolbarSlot: ReactNode;
  analytics: ProspectAnalytics;
  analyticsLoading: boolean;
  segment: Segment;
  onSegmentChange: (s: Segment) => void;

  prospects: ProspectLoan[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  totalCount?: number;
  sortConfig: ProspectSortConfig;
  onSort: (field: string) => void;

  selectedIds: string[];
  onSelectIds: (ids: string[]) => void;

  onViewDetails: (prospect: ProspectLoan) => void;
  onViewDocuments: (prospect: ProspectLoan) => void;
  onViewInvestorLock?: (prospect: ProspectLoan) => void;
  onAssignLO?: (prospectId: string) => void;
  onLoadMore?: () => void;
}

export function LoanCenterRedesign({
  direction,
  directionTabsSlot,
  toolbarSlot,
  analytics,
  analyticsLoading,
  segment,
  onSegmentChange,
  prospects,
  isLoading,
  isLoadingMore,
  hasMore,
  totalCount,
  sortConfig,
  onSort,
  selectedIds,
  onSelectIds,
  onViewDetails,
  onViewDocuments,
  onViewInvestorLock,
  onAssignLO,
  onLoadMore
}: LoanCenterRedesignProps) {
  // While server preference is unresolved, direction is null — render a skeleton
  // for the direction-specific content so the wrong view never flashes. Per the
  // approved plan's "no flash-of-wrong-view" invariant.
  const showSkeleton = direction === null;

  // Every block below sizes itself from the width of this container, so the
  // list fits beside the sidebar, without it, and at any window size.
  const { ref: containerRef, layout } = useLoanListLayout<HTMLDivElement>();

  return (
    <div ref={containerRef} className="flex min-w-0 flex-col gap-3">
      {directionTabsSlot}

      <SegmentBar
        analytics={analytics}
        segment={segment}
        onSegmentChange={onSegmentChange}
        isLoading={analyticsLoading}
        columns={layout.segmentColumns}
      />

      {toolbarSlot}

      {showSkeleton ? (
        <div className="border rounded-lg p-4 space-y-2 bg-background">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-14 animate-pulse rounded bg-muted/50" />
          ))}
        </div>
      ) : direction === 'table' ? (
        <TableDirection
          prospects={prospects}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          totalCount={totalCount}
          sortConfig={sortConfig}
          onSort={onSort}
          selectedIds={selectedIds}
          onSelectIds={onSelectIds}
          onViewDetails={onViewDetails}
          onViewDocuments={onViewDocuments}
          onViewInvestorLock={onViewInvestorLock}
          onAssignLO={onAssignLO}
          onLoadMore={onLoadMore}
          layout={layout.table}
          cardColumns={layout.cardColumns}
        />
      ) : direction === 'pipeline' ? (
        <PipelineDirection
          prospects={prospects}
          isLoading={isLoading}
          onViewDetails={onViewDetails}
          columns={layout.pipelineColumns}
        />
      ) : (
        <HybridDirection
          prospects={prospects}
          isLoading={isLoading}
          sortConfig={sortConfig}
          onSort={onSort}
          onViewDetails={onViewDetails}
          layout={layout.hybrid}
          cardColumns={layout.cardColumns}
        />
      )}
    </div>
  );
}
